import React, {useEffect, useState} from "react";
import {Skeleton, Box, Paper, Stack, useTheme, alpha} from "@mui/material";
import {useNavigate, useLocation} from "react-router-dom";
import CustomButton from "../../../core/components/ui/CustomButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CustomTypography from "../../../core/components/ui/CustomTypography";
import CustomChipComponent from "../../../core/components/common/custom-chip.component";
import moment from "moment";
import useContainer from "../hooks/useContainer";

const ViewContainerComponent = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();

    const {isViewingContainer, containerDetails, viewContainer} = useContainer();

    const [containerDetailsData, setContainerDetailsData] = useState(null);

    const containerId = new URLSearchParams(location.search).get("id");
    const rbacStorage = JSON.parse(localStorage.getItem("rbac-storage"));
    const storedOrgIdFromRBACForBusiness = rbacStorage?.state?.currentProfile?.orgId || null;
    const orgIdFromUrlForPortEmployee = new URLSearchParams(location.search).get("org_uid");


    useEffect(() => {
        const organizationId = storedOrgIdFromRBACForBusiness || orgIdFromUrlForPortEmployee; 

        if (organizationId) {
            const parameters = {
                containerUID: containerId,
                orgUID: organizationId
            };
    
            viewContainer(parameters);
    
        } else {
            const parameters = {containerUID: containerId};
        
            viewContainer(parameters);
        }
    }, [containerId, storedOrgIdFromRBACForBusiness, orgIdFromUrlForPortEmployee]);
    
    useEffect(() => {
        if (containerDetails) {
            setContainerDetailsData(containerDetails);
        }
    }, [containerDetails])


    const handleBack = () => {
        if (orgIdFromUrlForPortEmployee) {
            navigate(`/organization/view?org_uid=${orgIdFromUrlForPortEmployee}`);

        } else {
            navigate("/containers");
        } 
    }

    
    return (
        <>  
            <CustomButton 
                variant="text" 
                startIcon={<ArrowBackIcon fontSize="small" />}
                label="Back to Container List"
                customStyles={{fontSize: 14, ml: -2}}
                onClick={handleBack}
            />

            <Box sx={{textAlign: "start", mt: 2}}>
                <CustomTypography 
                    variant="h3"
                    text={"Container Details"}
                    customStyles={{
                        marginBottom: 0, 
                    }}
                />

                <CustomTypography 
                    variant="subtitle1"
                    text={"View and manage container data"}
                    customStyles={{
                        marginBottom: 0, 
                    }}
                />
            </Box>

            <Paper elevation={0} sx={{border: `1px solid #e5edf6`, borderRadius: 2, padding: "24px 32px", mt: 3}}>
                <Stack direction={{xs: "column", md: "row"}} spacing={4}>
                        <Stack spacing={2} flex={1} direction={"column"}>
                            <Box>
                                <CustomTypography 
                                    variant="caption" 
                                    text={"Container Number"} 
                                    customStyles={{
                                        marginBottom: 0, 
                                    }}
                                />

                                {(isViewingContainer) ?
                                    <Skeleton variant="text" height="35px" width="70%" sx={{backgroundColor: `${alpha(theme.palette.primary.main, 0.09)}`}} />
                                :
                                    <CustomTypography
                                        variant="body1"
                                        text={(containerDetailsData?.container_number)}
                                        customStyles={{
                                            marginBottom: 0, 
                                            fontWeight: 500,
                                        }}
                                    />
                                }
                            </Box>

                            <Box>
                                <CustomTypography 
                                    variant="caption" 
                                    text={"Size & Type"} 
                                    customStyles={{
                                        marginBottom: 0, 
                                    }}
                                />

                                {(isViewingContainer) ?
                                    <Skeleton variant="text" height="35px" width="70%" sx={{backgroundColor: `${alpha(theme.palette.primary.main, 0.09)}`}} />
                                :
                                    <CustomTypography
                                        variant="body1"
                                        text={(containerDetailsData?.type)}
                                        customStyles={{
                                            marginBottom: 0, 
                                            fontWeight: 500,
                                        }}
                                    />
                                }
                            </Box>

                            {(containerDetailsData?.status === "Decommissioned") && 
                                <Box>
                                    <CustomTypography 
                                        variant="caption" 
                                        text={"Reason"} 
                                        customStyles={{
                                            marginBottom: 0, 
                                        }}
                                    />

                                    {(isViewingContainer) ?
                                        <Skeleton variant="text" height="35px" width="70%" sx={{backgroundColor: `${alpha(theme.palette.primary.main, 0.09)}`}} />
                                    :
                                        <CustomTypography
                                            variant="body1"
                                            text={(containerDetailsData?.purpose || "N / A")}
                                            customStyles={{
                                                marginBottom: 0, 
                                                fontWeight: 500,
                                            }}
                                        />
                                    }
                                </Box>
                            }
                        </Stack>

                        <Stack spacing={2} flex={1}>
                            <Box sx={{display: "inline-grid"}}>
                                <CustomTypography 
                                    variant="caption" 
                                    text={"Status"}
                                    customStyles={{
                                        marginBottom: 0, 
                                    }} 
                                />

                                {(isViewingContainer) ?
                                    <Skeleton variant="text" height="35px" width="70%" sx={{backgroundColor: `${alpha(theme.palette.primary.main, 0.09)}`}} />
                                :
                                    <CustomChipComponent category="containerStatus"
                                        label={(containerDetailsData?.status === "In Port") ? "In Port" 
                                            : 
                                                (containerDetailsData?.status === "In Transit") ? "In Transit"
                                            :
                                                (containerDetailsData?.status === "Scheduled") ? "Scheduled"
                                            :
                                                (containerDetailsData?.status === "In CY/CD") ? "In CY/CD"
                                            :
                                                (containerDetailsData?.status === "Out of Port") ? "Out of Port"
                                            :
                                                (containerDetailsData?.status === "Decommissioned") ? "Decommissioned"
                                            :
                                                containerDetailsData?.status
                                        }
                                    />
                                }
  
                            </Box>

                            <Box>
                                <CustomTypography 
                                    variant="caption" 
                                    text={"Date Created"} 
                                    customStyles={{
                                        marginBottom: 0, 
                                    }}
                                />

                                {(isViewingContainer) ?
                                    <Skeleton variant="text" height="35px" width="70%" sx={{backgroundColor: `${alpha(theme.palette.primary.main, 0.09)}`}} />
                                :
                                    <CustomTypography
                                        variant="body1"
                                        text={moment(containerDetailsData?.created_time).format("MM/DD/YYYY HH:mm A")}
                                        customStyles={{
                                            marginBottom: 0, 
                                            fontWeight: 500,
                                        }}
                                    />    
                                }  
                            </Box>
                        </Stack>
                </Stack>           
            </Paper>
        </>
    )
}

export default ViewContainerComponent